/**
 * Top-bar workspace switcher.
 *
 * Two flavors decided from the user's role + accesses:
 *
 *   admin                       → [Admin] [Agents] [Mail] (+ [Odoo])
 *     Toggles between the admin console (/admin/*), the agent chat surface
 *     (/chat), and the internal mailbox (/admin/mail).
 *
 *   non-admin                   → [Menu] [Agents] [Mail] (+ [Odoo])
 *     'Menu' routes to the first non-chat resource they have access to
 *     (sheet → folder → media calendar → support → CRM, in that order).
 *
 * Mail lives up here rather than in the left rail because it is universal —
 * every user has a mailbox — so it needs no access gate. Its route differs by
 * role (/admin/mail vs /mail), and because the admin path sits under /admin it
 * has to be excluded from the Admin tab's active check.
 */
import type { FolderSummary, SheetSummary } from '@peerless/shared';
import { useQuery } from '@tanstack/react-query';
import { Boxes, Hammer, LayoutGrid, Mailbox, MessageSquare, Workflow } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

import { useAuth } from '../../hooks/useAuth';
import { apiRequest } from '../../lib/api-client';
import { cn } from '../../lib/cn';
import { queryKeys } from '../../lib/query-keys';

/** Shared: is the Odoo module enabled AND available to this caller? */
function useOdooAccess(enabled: boolean): boolean {
  const q = useQuery({
    queryKey: queryKeys.odoo.moduleAccess(),
    queryFn: () => apiRequest<{ access: boolean }>('/odoo/access'),
    staleTime: 60_000,
    enabled,
  });
  return q.data?.access === true;
}

/** Shared: is the Automation module enabled AND available to this caller? */
function useAutomationAccess(enabled: boolean): boolean {
  const q = useQuery({
    queryKey: queryKeys.automation.moduleAccess(),
    queryFn: () => apiRequest<{ access: boolean }>('/automation/access'),
    staleTime: 60_000,
    enabled,
  });
  return q.data?.access === true;
}

export function WorkspaceSwitcher(): JSX.Element | null {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const hasOdoo = useOdooAccess(!!user);
  const hasAutomation = useAutomationAccess(!!user);

  if (!user) return null;

  if (user.role === 'admin') {
    const onAdmin = pathname.startsWith('/admin');
    const onOdoo = pathname.startsWith('/odoo');
    const onAutomation = pathname.startsWith('/automation');
    // /admin/mail is under /admin, so keep it out of the Admin tab's active check.
    const onMail = pathname.startsWith('/admin/mail');
    const items: SwitcherItem[] = [
      { to: '/admin/users', icon: Hammer, label: 'Admin', active: onAdmin && !onOdoo && !onMail },
      { to: '/chat', icon: MessageSquare, label: 'Agents', active: !onAdmin && !onOdoo && !onAutomation },
      { to: '/admin/mail', icon: Mailbox, label: 'Mail', active: onMail },
    ];
    if (hasOdoo) items.push({ to: '/odoo', icon: Boxes, label: 'Odoo', active: onOdoo });
    if (hasAutomation) {
      items.push({ to: '/automation', icon: Workflow, label: 'Automation', active: onAutomation });
    }
    return <Switcher items={items} />;
  }

  return (
    <UserSwitcher pathname={pathname} userId={user.id} hasOdoo={hasOdoo} hasAutomation={hasAutomation} />
  );
}

/**
 * Non-admin user switcher. Queries the user's per-resource accesses to
 * decide (a) whether to render at all and (b) where 'Menu' should route
 * to. Anything more sophisticated than "first available" would mean
 * inventing a Menu landing page, which we don't have.
 */
function UserSwitcher({
  pathname,
  userId,
  hasOdoo,
  hasAutomation,
}: {
  pathname: string;
  userId: string;
  hasOdoo: boolean;
  hasAutomation: boolean;
}): JSX.Element | null {
  const sheetsQuery = useQuery({
    queryKey: queryKeys.userSheets.list(userId),
    queryFn: () => apiRequest<SheetSummary[]>(`/users/${userId}/sheets`),
    staleTime: 60_000,
  });
  const foldersQuery = useQuery({
    queryKey: queryKeys.userFolders.list(userId),
    queryFn: () => apiRequest<FolderSummary[]>(`/users/${userId}/folders`),
    staleTime: 60_000,
  });
  const mediaCalendarQuery = useQuery({
    queryKey: queryKeys.mediaCalendar.access(userId),
    queryFn: () => apiRequest<{ access: boolean }>('/media-calendar/access'),
    staleTime: 60_000,
  });
  const supportQuery = useQuery({
    queryKey: queryKeys.support.access(userId),
    queryFn: () => apiRequest<{ access: boolean }>('/support/access'),
    staleTime: 60_000,
  });
  const crmQuery = useQuery({
    queryKey: queryKeys.crm.access(userId),
    queryFn: () => apiRequest<{ access: boolean }>('/crm/access'),
    staleTime: 60_000,
  });
  const contentCalendarQuery = useQuery({
    queryKey: queryKeys.contentCalendar.access(userId),
    queryFn: () => apiRequest<{ access: boolean }>('/content-calendar/access'),
    staleTime: 60_000,
  });
  const visualsGeneratorQuery = useQuery({
    queryKey: queryKeys.visualsGenerator.access(userId),
    queryFn: () => apiRequest<{ access: boolean }>('/visuals-generator/access'),
    staleTime: 60_000,
  });
  const templateFillQuery = useQuery({
    queryKey: queryKeys.templateFill.access(userId),
    queryFn: () => apiRequest<{ access: boolean }>('/template-fill/access'),
    staleTime: 60_000,
  });
  const leadsQuery = useQuery({
    queryKey: queryKeys.leads.access(userId),
    queryFn: () => apiRequest<{ access: boolean }>('/leads/access'),
    staleTime: 60_000,
  });

  const sheets = (sheetsQuery.data ?? []).filter((s) => s.isActive);
  const folders = (foldersQuery.data ?? []).filter((f) => f.isActive);
  const hasMediaCalendar = mediaCalendarQuery.data?.access === true;
  const hasSupport = supportQuery.data?.access === true;
  const hasCrm = crmQuery.data?.access === true;
  const hasContentCalendar = contentCalendarQuery.data?.access === true;
  const hasVisualsGenerator = visualsGeneratorQuery.data?.access === true;
  const hasTemplateFill = templateFillQuery.data?.access === true;
  const hasLeads = leadsQuery.data?.access === true;

  // Mail is universal — every user has it by default, so the switcher
  // always has at least `/mail` to fall back to.
  const menuTarget =
    sheets[0] != null
      ? `/sheets/${sheets[0].id}`
      : folders[0] != null
        ? `/files/${folders[0].id}`
        : hasMediaCalendar
          ? '/media-calendar'
          : hasContentCalendar
            ? '/content-calendar'
            : hasVisualsGenerator
              ? '/visuals-generator'
              : hasTemplateFill
                ? '/template-fill'
                : hasSupport
                  ? '/support'
                  : hasCrm
                    ? '/crm'
                    : hasLeads
                      ? '/leads'
                      : '/mail';

  const onChats = pathname.startsWith('/chat');
  const onOdoo = pathname.startsWith('/odoo');
  const onAutomation = pathname.startsWith('/automation');
  const onMail = pathname.startsWith('/mail');

  const items: SwitcherItem[] = [
    {
      to: menuTarget,
      icon: LayoutGrid,
      label: 'Menu',
      active: !onChats && !onOdoo && !onMail && !onAutomation,
    },
    { to: '/chat', icon: MessageSquare, label: 'Agents', active: onChats },
    { to: '/mail', icon: Mailbox, label: 'Mail', active: onMail },
  ];
  if (hasOdoo) items.push({ to: '/odoo', icon: Boxes, label: 'Odoo', active: onOdoo });
  if (hasAutomation) {
    items.push({ to: '/automation', icon: Workflow, label: 'Automation', active: onAutomation });
  }

  return <Switcher items={items} />;
}

interface SwitcherItem {
  to: string;
  icon: typeof Hammer;
  label: string;
  active: boolean;
}

function Switcher({ items }: { items: SwitcherItem[] }): JSX.Element {
  return (
    <div
      role="tablist"
      aria-label="Workspace"
      className="inline-flex items-center rounded-tight border border-rule bg-paper-soft p-0.5"
    >
      {items.map((item) => (
        <Link
          key={item.to + item.label}
          to={item.to}
          role="tab"
          aria-selected={item.active}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-xs px-2.5 py-1 text-xs font-medium ' +
              'transition-colors duration-instant ease-standard',
            item.active
              ? 'bg-paper text-ink shadow-popover'
              : 'text-ink-muted hover:text-ink',
          )}
        >
          <item.icon className="size-3.5" />
          {item.label}
        </Link>
      ))}
    </div>
  );
}
